/**
 * Auto-composed + manual preliminary-charges editor for a draft job.
 *
 * - Auto track: 1 base diagnostic line + (optionally) 1 express add-on line,
 *   derived from (deviceType, serviceTier, urgency). The form recomputes
 *   these when any of those three change. Manual edits to quantity/unitCost
 *   are kept *if the productCode stays the same* across picker changes.
 *
 * - Manual track: rows added via [+ Add line item by hand], persisted across
 *   picker changes.
 *
 * Prices come from useProductCatalog. Missing-from-catalog SKUs render with
 * a warning chip and unitCost=0 (manually-overridable).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button } from '@mui/material';
import {
  IconPlus,
  IconTrash,
  IconDiscount2,
  IconCurrencyDollar,
} from '@tabler/icons-react';
import type {
  CheckInChargeLine,
  CheckInDeviceType,
  CheckInIntakeType,
} from '@techgitter/contracts';
import { DISC_RETURN_SKU } from '@techgitter/contracts';

import { Chip } from '../chip';
import {
  CHECK_IN_EXPEDITE_TIERS,
  CHECK_IN_SERVICE_TIERS,
  type CheckInExpediteTier,
  type CheckInServiceMode,
  type CheckInServiceTier,
} from '../../data/checkInServices';
import { getProductBySku, useProductCatalog } from '../../hooks/useProductCatalog';
import { useAppSettings } from '../../hooks/useAppSettings';
import { useWorkItem } from '../../hooks/useWorkItemData';
import './PreliminaryChargesEditor.css';

export interface PreliminaryChargesEditorProps {
  deviceType: CheckInDeviceType;
  serviceMode: CheckInServiceMode;
  serviceTier: CheckInServiceTier['id'];
  urgency: CheckInExpediteTier['id'];
  charges: CheckInChargeLine[];
  onChange: (charges: CheckInChargeLine[]) => void;
  /** Intake type of the parent check-in session. Drives the
   *  return-grace logic (`'in_store_return'`) and the drop-off
   *  diagnostic suppression (`'in_store_dropoff'`). */
  intakeType?: CheckInIntakeType | null;
  /** Dataverse id of the work item this session is linked to — the
   *  original closed job for a return (we read its `resolvedAt` to
   *  anchor the grace window) or the open in-progress job for a
   *  drop-off. */
  originalWorkItemId?: string | null;
  /** True when the picked customer's designation is `Core` or `Core+`.
   *  These tiers default to no diagnostic fee — their work rolls up to
   *  a monthly statement — but the receptionist can opt in per-device
   *  via the "Apply diagnostic fee" button. Customers without that
   *  designation (residential, un-designated business, etc.) pay the
   *  standard diag fee. */
  isCoreCustomer?: boolean;
}

const AUTO_BASE_KEY = '__auto_base';
const AUTO_SERVICE_KEY = '__auto_service';
const AUTO_EXPRESS_KEY = '__auto_express';
const RETURN_WAIVER_KEY_PREFIX = 'return-waiver-';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

interface ChargeWithRole extends CheckInChargeLine {
  /** Internal-only marker — never sent to the API. */
  __role?: 'auto_base' | 'auto_service' | 'auto_express' | 'manual';
}

export const PreliminaryChargesEditor: React.FC<PreliminaryChargesEditorProps> = ({
  deviceType,
  serviceMode,
  serviceTier,
  urgency,
  charges,
  onChange,
  intakeType = null,
  originalWorkItemId = null,
  isCoreCustomer = false,
}) => {
  const { products, isLoading: catalogLoading } = useProductCatalog();
  // Map for synchronous lookups after the catalog has loaded
  void products; // suppress unused warning — we use getProductBySku
  const previousKeyRef = useRef<{
    baseSku: string | null;
    serviceSku: string | null;
    expressSku: string | null;
  }>({
    baseSku: '',
    serviceSku: null,
    expressSku: null,
  });

  // Return-grace context. Only engaged when the parent passes both
  // `intakeType='in_store_return'` and an `originalWorkItemId`; for
  // every other intake the editor's existing behaviour is unchanged.
  const isReturn = intakeType === 'in_store_return';
  // Drop-off: the customer is handing over items for an already-agreed,
  // in-progress job (device back after parts arrived, or an extra part /
  // paperwork the job needs). Charges were settled on the original job, so
  // we never auto-seed a fresh diagnostic fee here.
  const isDropoff = intakeType === 'in_store_dropoff';
  const { settings: appSettings } = useAppSettings();
  const returnGraceDays = appSettings?.returnGraceDays ?? null;
  // Fetch the original work item only when it's actually a return.
  // `useWorkItem('')` is a no-op so this stays cheap on non-returns.
  const { workItem: originalWorkItem } = useWorkItem(
    isReturn ? (originalWorkItemId ?? '') : '',
  );
  const originalResolvedAt = originalWorkItem?.resolvedAt ?? null;

  // Compute the grace status:
  //  - 'unknown'     — we're a return but the resolvedAt isn't loaded
  //                    yet OR no admin grace days configured. Editor
  //                    behaves like a normal (non-return) check-in.
  //  - 'within'      — return + resolvedAt + within graceDays of now.
  //                    Auto-base diag is suppressed.
  //  - 'outside'     — return + resolvedAt + outside the window. Diag
  //                    seeds normally + a Waive button is exposed.
  const graceStatus: 'unknown' | 'within' | 'outside' = useMemo(() => {
    if (!isReturn) return 'unknown';
    if (returnGraceDays === null || returnGraceDays === undefined) {
      return 'unknown';
    }
    if (!originalResolvedAt) return 'unknown';
    const resolvedMs = new Date(originalResolvedAt).getTime();
    if (!Number.isFinite(resolvedMs)) return 'unknown';
    const elapsedDays = (Date.now() - resolvedMs) / MS_PER_DAY;
    return elapsedDays <= returnGraceDays ? 'within' : 'outside';
  }, [isReturn, returnGraceDays, originalResolvedAt]);

  // Core / Core+ customers default to no diagnostic fee (their repairs
  // roll up to a monthly statement). The receptionist can opt back in
  // for this draft job via the "Apply diagnostic fee" button — that
  // flips `chargeCoreDiagnostic` on and the auto-base line resumes.
  const [chargeCoreDiagnostic, setChargeCoreDiagnostic] = useState(false);
  // Reset the opt-in if the customer-designation signal flips back to a
  // non-Core tier (e.g. user picked a different customer). When a Core
  // customer is later picked again the default should be no-charge,
  // not stale-opt-in.
  useEffect(() => {
    if (!isCoreCustomer) setChargeCoreDiagnostic(false);
  }, [isCoreCustomer]);
  const coreSuppress = isCoreCustomer && !chargeCoreDiagnostic;
  const suppressDiagnostic = graceStatus === 'within' || coreSuppress || isDropoff;

  // Resolve the expected base + express SKUs from the pickers
  const { baseSku, serviceSku, expressSku } = useMemo(() => {
    const tier = CHECK_IN_SERVICE_TIERS.find(t => t.id === serviceTier) ?? CHECK_IN_SERVICE_TIERS[0];
    const expedite = CHECK_IN_EXPEDITE_TIERS.find(t => t.id === urgency) ?? CHECK_IN_EXPEDITE_TIERS[0];
    return {
      baseSku: tier.productCodeByDeviceType?.[deviceType] ?? null,
      serviceSku: tier.prepaidServiceSku ?? null,
      expressSku: expedite.productCode,
    };
  }, [serviceTier, urgency, deviceType]);

  // When picker state changes, recompose the auto lines.
  // Preserve overrides when the SKU is unchanged.
  useEffect(() => {
    if (catalogLoading) return;

    const previousKey = previousKeyRef.current;
    const next: ChargeWithRole[] = [];

    // Auto base line — only when the receptionist is taking a diagnostic fee.
    // In pre-paid-in-full mode the customer is paying for a known service
    // upfront, so we drop the diagnostic and let them enter the real charges
    // as manual line items.
    //
    // Returns within the admin-configured grace window are also exempt
    // (suppressDiagnostic) — the customer brought the device back
    // shortly after collecting it, so we don't re-charge for diagnosis.
    if (serviceMode === 'diagnose_and_quote' && !suppressDiagnostic && baseSku) {
      const existingBase = charges.find(c => c.id === AUTO_BASE_KEY);
      const baseSkuChanged = previousKey.baseSku && previousKey.baseSku !== baseSku;
      const baseLine = composeAutoLine(
        AUTO_BASE_KEY,
        baseSku,
        existingBase,
        'auto_base',
        baseSkuChanged,
      );
      next.push(baseLine);
    }

    // Auto pre-paid service line (e.g. Setup) — a flat service fee added
    // whenever the tier carries one, regardless of service mode.
    if (serviceSku) {
      const existingService = charges.find(c => c.id === AUTO_SERVICE_KEY);
      const serviceSkuChanged = previousKey.serviceSku !== serviceSku;
      const serviceLine = composeAutoLine(
        AUTO_SERVICE_KEY,
        serviceSku,
        existingService,
        'auto_service',
        serviceSkuChanged,
      );
      next.push(serviceLine);
    }

    // Auto express line (independent of service mode — pre-paid jobs can
    // still be expedited).
    if (expressSku) {
      const existingExpress = charges.find(c => c.id === AUTO_EXPRESS_KEY);
      const expressSkuChanged = previousKey.expressSku !== expressSku;
      const expressLine = composeAutoLine(
        AUTO_EXPRESS_KEY,
        expressSku,
        existingExpress,
        'auto_express',
        expressSkuChanged,
      );
      next.push(expressLine);
    }

    // Carry through any manual rows
    for (const c of charges) {
      if (c.id !== AUTO_BASE_KEY && c.id !== AUTO_SERVICE_KEY && c.id !== AUTO_EXPRESS_KEY) {
        next.push({ ...(c as ChargeWithRole), __role: 'manual' });
      }
    }

    previousKeyRef.current = { baseSku, serviceSku, expressSku };
    onChange(next.map(stripRole));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceType, serviceMode, serviceTier, urgency, catalogLoading, suppressDiagnostic]);

  const updateLine = (lineId: string, patch: Partial<CheckInChargeLine>) => {
    onChange(charges.map(c => {
      if (c.id !== lineId) return c;
      const merged = { ...c, ...patch };
      merged.subtotal = merged.quantity * merged.unitCost;
      merged.finalTotal = merged.subtotal;
      return merged;
    }));
  };

  const addManualLine = () => {
    const newLine: CheckInChargeLine = {
      id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      lineItemId: '',
      category: 'manual',
      quantity: 1,
      unitCost: 0,
      prefixUserBase: '',
      afterHours: false,
      nonChargeable: false,
      lineDetail: '',
      subtotal: 0,
      finalTotal: 0,
    };
    onChange([...charges, newLine]);
  };

  const removeManualLine = (lineId: string) => {
    onChange(charges.filter(c => c.id !== lineId));
  };

  const total = charges.reduce((sum, c) => sum + (c.finalTotal || 0), 0);

  // Waive-action availability — outside the grace window, with a diag
  // line present and no DISC.RETURN line already in place.
  const diagnosticLine = charges.find((c) => c.id === AUTO_BASE_KEY);
  const hasReturnWaiver = charges.some(
    (c) => c.lineItemId === DISC_RETURN_SKU,
  );
  const canWaiveDiagnostic =
    isReturn &&
    graceStatus === 'outside' &&
    !!diagnosticLine &&
    diagnosticLine.subtotal > 0 &&
    !hasReturnWaiver;

  const handleWaiveDiagnostic = () => {
    if (!diagnosticLine) return;
    const waiver: CheckInChargeLine = {
      id: `${RETURN_WAIVER_KEY_PREFIX}${Date.now()}`,
      lineItemId: DISC_RETURN_SKU,
      category: 'manual',
      quantity: 1,
      unitCost: -diagnosticLine.subtotal,
      prefixUserBase: '',
      afterHours: false,
      nonChargeable: false,
      lineDetail: 'Return diagnostic waived (post-grace)',
      subtotal: -diagnosticLine.subtotal,
      finalTotal: -diagnosticLine.subtotal,
    };
    onChange([...charges, waiver]);
  };

  // An auto-added code the catalog doesn't have composes at $0 and would
  // reach RepairShopr as a free-text line with no price. Say so here, while
  // reception can still see it — most likely the product hasn't been created
  // in RepairShopr yet (or hasn't synced).
  const unpricedSkus = catalogLoading
    ? []
    : charges
        .filter(c => c.id === AUTO_BASE_KEY || c.id === AUTO_SERVICE_KEY || c.id === AUTO_EXPRESS_KEY)
        .map(c => c.lineItemId)
        .filter(sku => !!sku && !getProductBySku(sku));

  return (
    <div className="prelim-charges">
      {/* Same heading as Device Type / Service Tier / Urgency above it: this is
          one more section of the device card, not a panel of its own. */}
      <div className="editor-section-title">Preliminary Charges</div>

      {unpricedSkus.length > 0 && (
        <Alert severity="warning">
          {unpricedSkus.join(', ')} {unpricedSkus.length === 1 ? 'isn’t' : 'aren’t'} in
          the product catalog, so the charge shows as $0. Add the product in
          RepairShopr, or set the price on the line by hand.
        </Alert>
      )}
      {/* Return-grace banner — only on return check-ins. Shows the
          current status so the receptionist knows why the diag was (or
          wasn't) auto-charged. */}
      {isReturn && graceStatus === 'within' && (
        <Alert severity="info" variant="outlined">
          Return within {returnGraceDays}-day grace period — diagnostic
          charge auto-waived.
        </Alert>
      )}
      {isReturn && graceStatus === 'outside' && hasReturnWaiver && (
        <Alert severity="success" variant="outlined">
          Diagnostic waived via {DISC_RETURN_SKU} adjustment.
        </Alert>
      )}
      {isDropoff && (
        <Alert severity="info" variant="outlined">
          Drop-off for an in-progress job — no diagnostic fee charged.
          Charges stay on the original job.
        </Alert>
      )}
      {coreSuppress && serviceMode === 'diagnose_and_quote' && (
        <Alert severity="info" variant="outlined">
          Core / Core+ customer — diagnostic fee waived by default.
          Repairs go to the monthly statement.
        </Alert>
      )}
      {isCoreCustomer && chargeCoreDiagnostic && serviceMode === 'diagnose_and_quote' && (
        <Alert
          severity="warning"
          variant="outlined"
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => setChargeCoreDiagnostic(false)}
            >
              Re-waive
            </Button>
          }
        >
          Diagnostic fee re-applied for this Core / Core+ customer.
        </Alert>
      )}

      <div className="prelim-charges__rows">
        {charges.map(c => {
          const isAuto =
            c.id === AUTO_BASE_KEY || c.id === AUTO_SERVICE_KEY || c.id === AUTO_EXPRESS_KEY;
          const product = c.lineItemId ? getProductBySku(c.lineItemId) : undefined;
          const priceMissing = !!c.lineItemId && !product;
          return (
            <div className="prelim-charges__row" key={c.id}>
              <div className="prelim-charges__product-cell">
                {isAuto ? (
                  <span className="prelim-charges__product">
                    {c.lineItemId || '(no product code)'}
                  </span>
                ) : (
                  <input
                    className="prelim-charges__input"
                    placeholder="Product code"
                    value={c.lineItemId}
                    onChange={e => updateLine(c.id, { lineItemId: e.target.value })}
                  />
                )}
                {priceMissing && (
                  <span className="prelim-charges__warn">
                    <Chip tone="warning">Price not in catalog</Chip>
                  </span>
                )}
              </div>
              <div className="prelim-charges__cost">
                <span className="prelim-charges__cost-prefix">$</span>
                <MoneyInput
                  value={c.unitCost}
                  ariaLabel={`Price for ${c.lineItemId || 'line'}`}
                  onChange={unitCost => updateLine(c.id, { unitCost })}
                />
              </div>
              {isAuto ? (
                <span className="prelim-charges__row-spacer" />
              ) : (
                <button
                  type="button"
                  className="editor-icon-btn editor-icon-btn--danger"
                  aria-label="Remove line"
                  onClick={() => removeManualLine(c.id)}
                >
                  <IconTrash size={16} stroke={1.5} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Same grid as the rows, so the total sits under the amounts. */}
      <div className="prelim-charges__row prelim-charges__total-row">
        <span className="prelim-charges__total-label">Total</span>
        <span className="prelim-charges__total-value">
          {total.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}
        </span>
        <span className="prelim-charges__row-spacer" />
      </div>

      <div className="prelim-charges__actions">
        <Button
          size="small"
          variant="text"
          startIcon={<IconPlus size={15} stroke={1.5} />}
          onClick={addManualLine}
        >
          Add line item
        </Button>
        {canWaiveDiagnostic && (
          <Button
            size="small"
            variant="text"
            color="warning"
            startIcon={<IconDiscount2 size={15} stroke={1.5} />}
            onClick={handleWaiveDiagnostic}
          >
            Waive diagnostic ({DISC_RETURN_SKU})
          </Button>
        )}
        {coreSuppress && serviceMode === 'diagnose_and_quote' && (
          <Button
            size="small"
            variant="text"
            startIcon={<IconCurrencyDollar size={15} stroke={1.5} />}
            onClick={() => setChargeCoreDiagnostic(true)}
          >
            Apply diagnostic fee
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * A dollar amount that reads like the total under it ("90.00", not "90").
 * Formatted to two places at rest; while focused it holds the text as typed,
 * so a half-typed "9." isn't snapped back mid-edit. Text with
 * `inputMode="decimal"` rather than `type="number"`: a number input can't hold
 * a formatted value, and its spinners nudge prices on a stray scroll.
 */
const MoneyInput: React.FC<{
  value: number;
  ariaLabel: string;
  onChange: (value: number) => void;
}> = ({ value, ariaLabel, onChange }) => {
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <input
      className="prelim-charges__input prelim-charges__cost-input"
      inputMode="decimal"
      aria-label={ariaLabel}
      value={editing ?? (Number.isFinite(value) ? value.toFixed(2) : '0.00')}
      onFocus={e => {
        setEditing(e.target.value);
        e.target.select();
      }}
      onChange={e => {
        const text = e.target.value.replace(/[^0-9.]/g, '');
        setEditing(text);
        const parsed = Number(text);
        onChange(Number.isFinite(parsed) ? Math.max(0, parsed) : 0);
      }}
      onBlur={() => setEditing(null)}
    />
  );
};

function composeAutoLine(
  id: string,
  sku: string,
  existing: ChargeWithRole | CheckInChargeLine | undefined,
  role: 'auto_base' | 'auto_service' | 'auto_express',
  skuChanged: boolean,
): ChargeWithRole {
  // If the SKU is unchanged from a previous render, preserve user overrides.
  if (existing && existing.lineItemId === sku && !skuChanged) {
    return { ...existing, __role: role };
  }
  const product = getProductBySku(sku);
  const unitCost = product?.unitCost ?? 0;
  return {
    id,
    lineItemId: sku,
    category: role === 'auto_base' ? 'diagnostic' : role === 'auto_service' ? 'service' : 'expedite',
    quantity: 1,
    unitCost,
    prefixUserBase: '',
    afterHours: false,
    nonChargeable: false,
    lineDetail: '',
    subtotal: unitCost,
    finalTotal: unitCost,
    __role: role,
  };
}

function stripRole(c: ChargeWithRole): CheckInChargeLine {
  const { __role, ...rest } = c;
  return rest;
}
